<template>
  <span class="x-icon" :class="classes" :style="styles" v-on="$listeners">
    <slot><component v-if="icon" :is="icon" :colors="colors"></component></slot>
  </span>
</template>

<script>
/**
 * 图标组件
 * @module XIcon
 */
const colors = ['primary', 'success', 'warning', 'danger'];

/**
 * 插槽
 * @member slots
 * @property {string} default 自定义图标内容
 */
export default {
  name: 'XIcon',
  /**
   * 属性参数
   * @member props
   * @property {string} [name] 图标名称，仅用用作css字体图标的样式名称
   * @property {string|number} [size] 图标尺寸，可选值：'default', 'large', 'small', 'mini'，数组表示像素
   * @property {string|string[]} [color] 颜色
   * @property {string|object|function} [icon] 图标，svg组件名称或组件对象
   */
  props: {
    name: {
      type: String
    },
    size: {
      type: [String, Number],
      default: 'default',
      validator(val) {
        if (typeof val === 'string') {
          return ['default', 'large', 'small', 'mini'].includes(val);
        }
        return val > 0;
      }
    },
    color: {
      type: [String, Array]
    },
    icon: [String, Object, Function]
  },
  computed: {
    colors() {
      const color = this.color;
      if (!color) return [];
      return Array.isArray(this.color) ? color : [color];
    },
    classes() {
      const cls = [];
      if (typeof this.name === 'string') {
        cls.push(this.name);
      }
      if (typeof this.size === 'string') {
        cls.push(`is-${this.size}`);
      }
      if (this.$listeners.click) {
        cls.push('is-pointer');
      }
      if (colors.includes(this.colors[0])) {
        cls.push(`is-${this.colors[0]}`);
      }
      return cls;
    },
    styles() {
      const style = {};
      if (typeof this.size === 'number') {
        const value = `${this.size}px`;
        Object.assign(style, {
          fontSize: value,
          width: value,
          height: value
        });
      }
      const color = this.colors[0];
      if (color && !colors.includes(color)) {
        style.color = color;
      }
      return style;
    }
  }
};
/**
 * 点击触发
 * @event click
 */
</script>
<style lang="scss">
@import "../../style/mixins/index";

@mixin size($size, $value) {
  @include when($size) {
    font-size: $value;
    width: $value;
    height: $value;
  }
}

@mixin color($type,$value) {
  @include when($type) {
    color: $value;
  }
}

@include b(icon) {
  overflow: hidden;
  display: inline-block;
  vertical-align: middle !important;
  font-size: $--x-icon-size-default;
  width: $--x-icon-size-default;
  height: $--x-icon-size-default;
  & + .x-icon {
    margin-left: $--x-icon-split;
  }
  > img, > svg {
    width: 100%;
    height: 100%;
    vertical-align: top;
    display: inline-block;
  }
  @include when(pointer) {
    cursor: pointer;
    &:hover {
      opacity: $--x-icon-pointer-opacity;
    }
  }
  @include size(large, $--x-icon-size-large);
  @include size(small, $--x-icon-size-small);
  @include size(mini, $--x-icon-size-mini);
  @include color(primary, $--x-icon-color-primary);
  @include color(success, $--x-icon-color-success);
  @include color(warning, $--x-icon-color-warning);
  @include color(danger, $--x-icon-color-danger);
}
</style>